// Pure helpers for the listing photo gallery (Airbnb-style overlay).
// Kept free of React so they are trivially unit-testable.

/**
 * Splits images into alternating layout rows: 1 full-width image, then 2
 * half-width images, repeating. The trailing row may be partially filled.
 * Each item keeps its global index into the original array so a click can
 * open the carousel at the right photo.
 *
 * @param {Array<object>} images
 * @returns {Array<{type: 'full'|'pair', items: Array<{image: object, index: number}>}>}
 */
export const chunkGalleryImages = (images) => {
  const rows = [];
  let index = 0;
  let full = true;
  while (index < images.length) {
    const take = full ? 1 : 2;
    rows.push({
      type: full ? 'full' : 'pair',
      items: images.slice(index, index + take).map((image, offset) => ({
        image,
        index: index + offset,
      })),
    });
    index += take;
    full = !full;
  }
  return rows;
};

export const GALLERY_HASH = '#gallery';
const PHOTO_HASH_PREFIX = '#gallery-photo-';

/**
 * Builds the hash for an enlarged photo at the given image index.
 *
 * @param {number} index
 * @returns {string}
 */
export const photoHash = (index) => `${PHOTO_HASH_PREFIX}${index}`;

/**
 * Derives gallery state from a location hash.
 * Invalid photo indices (NaN, non-integer, out of bounds) degrade to
 * "gallery open, no photo" instead of breaking the overlay.
 *
 * @param {string|null|undefined} hash - location.hash including leading '#' (or '')
 * @param {number} imageCount
 * @returns {{galleryOpen: boolean, photoIndex: number|null}}
 */
export const parseGalleryHash = (hash, imageCount) => {
  if (hash === GALLERY_HASH) {
    return { galleryOpen: true, photoIndex: null };
  }
  if (hash?.startsWith(PHOTO_HASH_PREFIX)) {
    const raw = hash.slice(PHOTO_HASH_PREFIX.length);
    const index = Number(raw);
    const isValid = raw !== '' && Number.isInteger(index) && index >= 0 && index < imageCount;
    return { galleryOpen: true, photoIndex: isValid ? index : null };
  }
  return { galleryOpen: false, photoIndex: null };
};
